import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Avatar, Typography, Space, Badge, Row, Col, Collapse, Empty, Checkbox, message } from "antd";
import { TeamOutlined, ApartmentOutlined, SearchOutlined, SaveOutlined, GlobalOutlined, DesktopOutlined, MobileOutlined } from "@ant-design/icons";
import { AntdButton, InputField } from "../../../blueplanitUtils/InputField";
import { updateEmployeeAccessType } from "../../../Slices/Thunk/employeeThunk";

const { Title, Text } = Typography;
const { Panel } = Collapse;

const DEVICE_OPTIONS = [
    { key: "WEB", label: "Web", icon: <GlobalOutlined />, color: "#3B82F6" },
    { key: "DESKTOP", label: "Desktop", icon: <DesktopOutlined />, color: "#8B5CF6" },
    { key: "MOBILE", label: "Mobile", icon: <MobileOutlined />, color: "#10B981" },
];

// Backward compat: convert old "BOTH" → ["WEB", "DESKTOP"]
const normalizeAccessType = (val) => {
    if (Array.isArray(val)) return val;
    if (val === "BOTH") return ["WEB", "DESKTOP"];
    if (val === "WEB" || val === "DESKTOP" || val === "MOBILE") return [val];
    return [];
};

const filterActiveEmployees = (emps) => emps?.filter((e) => e.employeestatus === "Active" && e.department?.trim()) || [];

export default function DeviceManagement() {
    const { AllEmployees: allEmployees } = useSelector((s) => s.employee);
    const loginUser = useSelector((s) => s?.loginUserInfo?.loginUserDetails);
    const dispatch = useDispatch();
    const [messageApi, contextHolder] = message.useMessage();
    const [initialDeviceMap, setInitialDeviceMap] = useState({});
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [employeeDeviceMap, setEmployeeDeviceMap] = useState({});
    const [searchTerm, setSearchTerm] = useState("");

    const employees = useMemo(() => filterActiveEmployees(allEmployees), [allEmployees]);

    const groupedFilteredEmployees = useMemo(() => {
        const filtered = !searchTerm
            ? employees
            : employees.filter((e) => {
                const lower = searchTerm.toLowerCase();
                return (
                    e.firstname?.toLowerCase().includes(lower) ||
                    e.lastname?.toLowerCase().includes(lower) ||
                    e.department?.toLowerCase().includes(lower)
                );
            });

        const grouped = {};
        filtered.forEach((e) => {
            if (!grouped[e.department]) grouped[e.department] = [];
            grouped[e.department].push(e);
        });

        return grouped;
    }, [employees, searchTerm]);

    const selectedEmp = useMemo(() => employees.find((e) => e.id === selectedEmployee), [employees, selectedEmployee]);

    const currentChecked = selectedEmployee
        ? (employeeDeviceMap[selectedEmployee] || [])
        : [];

    useEffect(() => {
        if (!selectedEmp) return;

        setEmployeeDeviceMap(prev => {
            if (prev[selectedEmp.id] !== undefined) return prev;

            const normalized = normalizeAccessType(selectedEmp.accessType);
            setInitialDeviceMap(p => ({ ...p, [selectedEmp.id]: normalized }));

            return { ...prev, [selectedEmp.id]: normalized };
        });
    }, [selectedEmp]);

    const onToggleDevice = useCallback((deviceKey) => {
        if (!selectedEmployee) return;
        setEmployeeDeviceMap(prev => {
            const current = prev[selectedEmployee] || [];
            const next = current.includes(deviceKey)
                ? current.filter(d => d !== deviceKey)
                : [...current, deviceKey];
            return { ...prev, [selectedEmployee]: next };
        });
    }, [selectedEmployee]);

    const handleSave = useCallback(async () => {
        if (!selectedEmp) {
            messageApi.error("Please select an employee");
            return;
        }

        const current = employeeDeviceMap[selectedEmp.id] || [];
        const initial = initialDeviceMap[selectedEmp.id] || [];

        if (JSON.stringify([...current].sort()) === JSON.stringify([...initial].sort())) {
            messageApi.error("No changes to save");
            return;
        }

        if (current.length === 0) {
            messageApi.error("At least one device must be selected");
            return;
        }

        try {
            await dispatch(
                updateEmployeeAccessType({
                    id: selectedEmp.id,
                    accessType: current,
                })
            ).unwrap();

            messageApi.success("Device access updated successfully");

            setInitialDeviceMap(prev => ({
                ...prev,
                [selectedEmp.id]: current
            }));
        } catch {
            messageApi.error("Failed to update device access");
        }
    }, [selectedEmp, employeeDeviceMap, initialDeviceMap, dispatch, messageApi]);

    return (
        <div style={styles.container}>
            {contextHolder}
            <div style={{ marginBottom: 24 }}>
                <Title level={3} style={{ margin: 0, marginBottom: 4 }}>
                    Device Management
                </Title>
                <Text type="secondary">
                    Manage which device each employee can access
                </Text>
            </div>
            <Row gutter={20}>
                <Col span={8}>
                    <Card style={styles.listCard} bodyStyle={{ padding: 16 }} title={
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <TeamOutlined />
                                <span>Select Employee</span>
                            </div>

                            <InputField
                                placeholder="Search employees..."
                                prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                                style={{ width: "100%" }}
                                size="middle"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                allowClear
                            />
                        </div>
                    }>
                        <div style={{ maxHeight: 600, overflowY: "auto" }}>
                            {Object.keys(groupedFilteredEmployees).length === 0 ? (
                                <Empty description="No employees found" style={{ padding: "40px 0" }} />
                            ) : (
                                <Collapse ghost defaultActiveKey={Object.keys(groupedFilteredEmployees)}>
                                    {Object.keys(groupedFilteredEmployees).map((deptName) => (
                                        <Panel header={
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                <Text strong>{deptName}</Text>
                                                <Badge count={groupedFilteredEmployees[deptName].length} style={{ backgroundColor: "#1677ff" }} />
                                            </div>
                                        } key={deptName}>
                                            {groupedFilteredEmployees[deptName].map((emp) => (
                                                <div key={emp.id} style={styles.employeeListItem(selectedEmployee === emp.id)} onClick={() => setSelectedEmployee(emp.id)}>
                                                    <Space size={12}>
                                                        <Avatar size={32} src={emp.image} style={{ background: "#1677ff" }}>
                                                            {emp.firstname?.charAt(0)}
                                                        </Avatar>
                                                        <div>
                                                            <Text strong style={{ color: "inherit", display: "block" }}>
                                                                {emp.firstname} {emp.lastname}
                                                            </Text>
                                                            <Text style={{ fontSize: 12, color: selectedEmployee === emp.id ? "rgba(255,255,255,0.8)" : "#8c8c8c" }}>
                                                                {emp.designation || emp.role}
                                                            </Text>
                                                        </div>
                                                    </Space>
                                                    {/* <Badge count={employeeDeviceMap[emp.id] ? 1 : 0} style={{ backgroundColor: selectedEmployee === emp.id ? "rgba(255,255,255,0.25)" : "#f0f0f0", color: selectedEmployee === emp.id ? "#fff" : "#595959" }} /> */}
                                                </div>
                                            ))}
                                        </Panel>
                                    ))}
                                </Collapse>
                            )}
                        </div>
                    </Card>
                </Col>

                <Col span={16}>
                    <Card style={styles.detailCard} bodyStyle={{ padding: 16 }} title={
                        selectedEmp ? (
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <Avatar size={40} src={selectedEmp.image} style={{ background: "#1677ff" }}>{selectedEmp.firstname?.charAt(0)}</Avatar>
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 15 }}>{selectedEmp.firstname} {selectedEmp.lastname}</div>
                                    <Text type="secondary" style={{ fontSize: 13 }}>{selectedEmp.designation || selectedEmp.role} • {selectedEmp.department}</Text>
                                </div>
                            </div>
                        ) : (
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <ApartmentOutlined />
                                <span>Device Assignment</span>
                            </div>
                        )
                    }>
                        {selectedEmp ? (
                            <div style={{ paddingTop: 12 }}>
                                <Text type="secondary">Select which apps this employee can access</Text>
                                <div style={{ marginTop: 16, display: "flex", gap: 16 }}>
                                    {DEVICE_OPTIONS.map(opt => {
                                        const isChecked = currentChecked.includes(opt.key);
                                        return (
                                            <Card
                                                key={opt.key}
                                                hoverable
                                                onClick={() => onToggleDevice(opt.key)}
                                                style={{
                                                    flex: 1,
                                                    borderRadius: 12,
                                                    border: isChecked ? `2px solid ${opt.color}` : "2px solid #f0f0f0",
                                                    background: isChecked ? `${opt.color}08` : "#fff",
                                                    cursor: "pointer",
                                                    transition: "all 0.2s",
                                                }}
                                                bodyStyle={{ padding: 20, textAlign: "center" }}
                                            >
                                                <div style={{ fontSize: 28, color: isChecked ? opt.color : "#bfbfbf", marginBottom: 8 }}>
                                                    {opt.icon}
                                                </div>
                                                <Text strong style={{ display: "block", fontSize: 14, color: isChecked ? opt.color : "#595959" }}>
                                                    {opt.label}
                                                </Text>
                                                <Checkbox
                                                    checked={isChecked}
                                                    style={{ marginTop: 12 }}
                                                    onChange={() => onToggleDevice(opt.key)}
                                                />
                                            </Card>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            <Empty description="Select an employee to assign device access" style={{ padding: "60px 0" }} />
                        )}
                    </Card>
                </Col>
            </Row>
            <Card style={{ marginTop: 20, borderRadius: 12 }}>
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}>
                    <Space>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#1677ff" }} />
                        <Text type="secondary">
                            Changes will be saved to employee device access
                        </Text>
                    </Space>

                    <AntdButton
                        type="primary"
                        size="large"
                        icon={<SaveOutlined />}
                        onClick={handleSave}
                        label="Save Access Settings"
                    />
                </div>
            </Card>
        </div>
    );
}

const styles = {
    container: { minHeight: "100vh", background: "#f5f5f5", padding: "24px" },
    listCard: { borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.08)", border: "none", height: "fit-content" },
    detailCard: { borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.08)", border: "none" },
    employeeListItem: (isSelected) => ({
        padding: "12px 16px",
        marginBottom: 8,
        borderRadius: 8,
        cursor: "pointer",
        background: isSelected ? "#1677ff" : "#fff",
        color: isSelected ? "#fff" : "#333",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
    })
};